import React, { useEffect, useState, useContext, useRef } from 'react'
import { CCard, CButton, CFormSelect, CFormInput } from '@coreui/react'
import SearchTable from 'src/components/searchTable'
import moment from 'moment'
import useAxios from 'axios-hooks'
import { isEmpty } from 'lodash'
import DateRangePicker from 'react-bootstrap-daterangepicker'
import 'bootstrap-daterangepicker/daterangepicker.css'
import { AppContext } from 'src/views/context/app'
import { ANALYTICS_API } from 'src/views/utils/urls'
import { PAGE_SIZE } from 'src/views/utils/constants'
import getSymbolFromCurrency from 'currency-symbol-map'

const UserSubscriptionReport = ({ subscriptionData }) => {
  const { setLoader } = useContext(AppContext)
  const dateRangeRef = useRef(null)
  const [startDate, setStartDate] = useState()
  const [endDate, setEndDate] = useState()
  const [subscriptionFilter, setSubscriptionFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [userSubscriptionList, setUserSubscriptionList] = useState([])

  const [{ loading: downloadExcelLoading, response: downloadExcelResponse }, downloadExcelCall] =
    useAxios(
      {
        url: `${ANALYTICS_API}/user-subscription-analytics/download`,
        method: 'get',
        responseType: 'arraybuffer',
      },
      { manual: true },
    )

  const applyFilter = () => {
    setPage(1)
    userSubscriptionCall({
      params: {
        page: 1,
        subscription_plan: subscriptionFilter,
        start_date: startDate,
        end_date: endDate,
        renewal_status: statusFilter,
      },
    })
  }

  const [
    { data: userSubscriptionData, loading: userSubscriptionDataLoading },
    userSubscriptionCall,
  ] = useAxios(
    {
      url: `${ANALYTICS_API}/user-subscription-analytics?order_by=last_renewal_date&page_size=${PAGE_SIZE}`,
      method: 'get',
    },
    { manual: true },
  )

  useEffect(() => {
    userSubscriptionCall({
      params: {
        page: page,
      },
    })
  }, [])

  useEffect(() => {
    setLoader(userSubscriptionDataLoading || downloadExcelLoading)
  }, [userSubscriptionDataLoading, downloadExcelLoading])

  useEffect(() => {
    if (userSubscriptionData && userSubscriptionData.success) {
      page === 1
        ? setUserSubscriptionList(userSubscriptionData.data)
        : setUserSubscriptionList((userSubscriptionList) => [
            ...userSubscriptionList,
            ...userSubscriptionData.data,
          ])
    }
  }, [userSubscriptionData])

  useEffect(() => {
    if (downloadExcelResponse && downloadExcelResponse.status === 200) {
      const blob = new Blob([downloadExcelResponse.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `user-subscription.xlsx`
      link.click()
    }
  }, [downloadExcelResponse])

  const resetFilters = () => {
    if (dateRangeRef.current) {
      dateRangeRef.current.$picker[0].value = ''
    }
    setPage(1)
    setStartDate('')
    setEndDate('')
    setSubscriptionFilter('')
    setStatusFilter('')
    userSubscriptionCall({
      params: {
        page: 1,
      },
    })
  }

  const tableConfig = [
    {
      header: 'User Email',
      dataCell: (info) => info.email_id,
      width: 11,
    },
    {
      header: 'Name',
      dataCell: (info) => info.user_name,
      width: 10,
    },
    {
      header: 'Plan',
      dataCell: (info) => info.plan_name,
      width: 12,
    },
    {
      header: 'Start Date',
      dataCell: (info) => moment.utc(info.start_date).format('DD-MM-YYYY'),
      width: 10,
    },
    {
      header: 'Next Renewal Date',
      dataCell: (info) => moment.utc(info.next_renewal_date).format('DD-MM-YYYY'),
      width: 13,
    },
    {
      header: 'Last Renewal Date',
      dataCell: (info) => moment.utc(info.last_renewal_date).format('DD-MM-YYYY'),
      width: 13,
    },
    {
      header: 'Status',
      dataCell: (info) => {
        switch (info.status) {
          case 'ongoing':
            return 'Active'
          case 'stop':
            return 'Cancelled'
          case 'failed':
            return 'Failed'
          default:
            return ''
        }
      },
      width: 9,
    },
    {
      header: '#Renewed',
      dataCell: (info) => info.times_renewed,
      width: 9,
    },
    {
      header: 'Revenue',
      dataCell: (info) =>
        `${Number(info.revenue).toFixed(2)}${
          info.currency ? getSymbolFromCurrency(info.currency) : ''
        }`,
      width: 9,
    },
  ]

  const handleDateChange = (event, picker) => {
    picker.element.val(
      picker.startDate.format('DD/MM/YYYY') + ' - ' + picker.endDate.format('DD/MM/YYYY'),
    )
    setStartDate(moment.utc(picker.startDate).format('YYYY-MM-DDTHH:mm:ss'))
    setEndDate(moment.utc(picker.endDate).format('YYYY-MM-DDTHH:mm:ss'))
  }

  const handleCancel = (event, picker) => {
    picker.element.val('')
    setStartDate('')
    setEndDate('')
  }

  const downloadExcel = () => {
    downloadExcelCall({
      params: {
        subscription_plan: subscriptionFilter,
        start_date: startDate,
        end_date: endDate,
        renewal_status: statusFilter,
      },
    })
  }

  const handleNextPage = () => {
    userSubscriptionCall({
      params: {
        page: page + 1,
        subscription_plan: subscriptionFilter,
        start_date: startDate,
        end_date: endDate,
        renewal_status: statusFilter,
      },
    })
    setPage((page) => page + 1)
  }

  return (
    <CCard>
      <div className="d-flex justify-content-between mt-3 py-2">
        <div className="d-flex">
          <p className="mt-1">Filters:</p>
          <div className="mx-2">
            <CFormSelect
              size="sm"
              placeholder="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="" selected disabled>
                Status
              </option>
              <option value={'ongoing'}>Active</option>
              <option value={'stop'}>Cancelled</option>
              <option value={'failed'}>Failed</option>
            </CFormSelect>
          </div>
          <div className="mx-3">
            <CFormSelect
              size="sm"
              placeholder="Subscription Plan"
              value={subscriptionFilter}
              onChange={(e) => setSubscriptionFilter(e.target.value)}
            >
              <option value="" disabled>
                Subscription Plan
              </option>
              {!isEmpty(subscriptionData) &&
                subscriptionData.map((info, index) => (
                  <option value={info._id} key={index}>
                    {info.name}
                  </option>
                ))}
            </CFormSelect>
          </div>
          <div className="mx-3">
            <DateRangePicker
              ref={dateRangeRef}
              initialSettings={{
                autoUpdateInput: false,
                locale: { format: 'DD/MM/YYYY', cancelLabel: 'Clear' },
                placeholder: 'Select Date Range',
              }}
              onApply={handleDateChange}
              onCancel={handleCancel}
            >
              <CFormInput
                size="sm"
                type="text"
                className=""
                defaultValue=""
                placeholder="Select Date Range"
              />
            </DateRangePicker>
          </div>
          <div className="mx-2">
            <CButton color="info" size="sm" className="text-white" onClick={applyFilter}>
              Apply
            </CButton>
          </div>
          <p
            className="mt-1"
            style={{ color: '#1976d2', cursor: 'pointer' }}
            onClick={resetFilters}
          >
            Reset Filter
          </p>
        </div>
        <div className="d-flex">
          <div className="mx-2">
            <CButton color="success" size="sm" className="text-white" onClick={downloadExcel}>
              Download Excel
            </CButton>
          </div>
        </div>
      </div>
      <div style={{ zIndex: '0' }}>
        <SearchTable
          type={`User Subscription Report`}
          tableCardHeight={63}
          disableButton={true}
          disableSearch={true}
          marginDisable={true}
          textAlign={'center'}
          tableContent={userSubscriptionList}
          tableConfig={tableConfig}
          hashWidth={4}
          handleNextPage={handleNextPage}
          paginationEnable={
            userSubscriptionData &&
            !isEmpty(userSubscriptionData.data) &&
            userSubscriptionData.total > page * 20
          }
          totalTableContent={userSubscriptionData?.total}
        />
      </div>
    </CCard>
  )
}

export default UserSubscriptionReport
